// Экран разрешения на передачу данных AI-сервисам — шаг входа в приложение.
//
// ┌─ Откуда ──────────────────────────────────────────────────────────────────┐
// │ App Store, guidelines 5.1.1(i) и 5.1.2(i), отказ 17.09.2026: приложение    │
// │ обязано ДО передачи данных стороннему AI сказать, что уходит и кому, и     │
// │ спросить разрешение. Упоминания в политике недостаточно.                  │
// │                                                                          │
// │ Экран появляется на каждой установке приложения один раз для каждого      │
// │ аккаунта — после принятия дисклеймера и раньше онбординга, где уже        │
// │ создаётся первый курс. Встроен в `AuthFlowGate`, а не всплывает модалкой: │
// │ модалку можно смахнуть, а за шагом входа ничего не отправляется.          │
// └──────────────────────────────────────────────────────────────────────────┘
//
// Обе кнопки равноправны по размеру: вопрос о согласии не должен подталкивать к
// ответу. «Не разрешать» не закрывает приложение — AI-функции выключаются
// сервером, остальное работает, решение меняется в «Приватности».

import { Button, NARROW_PAGE_SHELL } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import { IconShieldCheck } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type AiDataSharingDecision, useAiDataSharing } from "@/hooks/use-ai-data-sharing";
import { cn } from "@/lib/cn";
import { AiDataSharingDisclosure } from "./ai-data-sharing-disclosure";

export function AiDataSharingPrompt({
	onDecided,
}: {
	/** Решение записано на сервере — гейт отмечает установку и пропускает дальше. */
	onDecided: () => void;
}): React.ReactElement {
	const { t } = useTranslation("legal");
	const { decide } = useAiDataSharing();
	const [pending, setPending] = useState<AiDataSharingDecision | null>(null);

	const choose = (decision: AiDataSharingDecision): void => {
		if (pending) return;
		setPending(decision);
		decide(decision)
			.then(() => {
				if (decision === "denied") toast(t("aiDataSharing.deniedToast"));
				onDecided();
			})
			.catch(() => {
				// Решение не записано — экран остаётся: пропустить человека дальше
				// без ответа значило бы отправлять данные, не спросив.
				toast.error(t("aiDataSharing.saveError"));
			})
			.finally(() => setPending(null));
	};

	return (
		<div className="flex min-h-dvh flex-col bg-background pt-[env(safe-area-inset-top,0px)]">
			<main className={cn(NARROW_PAGE_SHELL, "flex-1")}>
				<div className="flex items-start gap-3">
					<div className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-tile)] bg-primary/10 [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]">
						<IconShieldCheck size={20} className="text-primary" stroke={1.75} />
					</div>
					<div className="min-w-0">
						<h1 className="text-balance text-2xl font-semibold tracking-tight">
							{t("aiDataSharing.title")}
						</h1>
						<p className="mt-1 text-sm leading-relaxed text-muted-foreground">
							{t("aiDataSharing.lead")}
						</p>
					</div>
				</div>

				<div className="mt-6">
					<AiDataSharingDisclosure />
				</div>

				<p className="mt-4 text-xs leading-relaxed text-muted-foreground">
					{t("aiDataSharing.changeLater")}
				</p>

				<div className="mt-6 grid grid-cols-1 gap-3 border-t border-border pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] sm:grid-cols-2">
					<Button
						size="lg"
						variant="outline"
						className="h-11 cursor-pointer disabled:cursor-not-allowed"
						loading={pending === "denied"}
						disabled={pending !== null}
						onClick={() => choose("denied")}
					>
						{t("aiDataSharing.deny")}
					</Button>
					<Button
						size="lg"
						className="h-11 cursor-pointer disabled:cursor-not-allowed"
						loading={pending === "granted"}
						disabled={pending !== null}
						onClick={() => choose("granted")}
					>
						{t("aiDataSharing.allow")}
					</Button>
				</div>
			</main>
		</div>
	);
}
